import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Typography, LinearProgress, Chip, keyframes } from '@mui/material';
import { PipelineNodeData, getStatusColor, getStatusIcon } from '../types/pipeline.ts';

interface PipelineStepNodeProps {
  data: PipelineNodeData;
  selected?: boolean;
}

// Pulse animation for in_progress state
const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7); }
  50% { box-shadow: 0 0 0 10px rgba(255, 152, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
`;

const PipelineStepNode: React.FC<PipelineStepNodeProps> = ({ data, selected }) => {
  const borderColor = getStatusColor(data.status);
  const statusIcon = getStatusIcon(data.status);

  // Calculate elapsed time if in progress
  const getElapsedTime = () => {
    if (data.status === 'in_progress' && data.startTime) {
      const elapsed = Math.floor((Date.now() - data.startTime) / 1000);
      return `${elapsed}s elapsed`;
    }
    return null;
  };

  const elapsedTime = getElapsedTime();

  return (
    <Box
      sx={{
        border: 3,
        borderColor: borderColor,
        borderRadius: 2,
        p: 2,
        minWidth: 220,
        maxWidth: 280,
        bgcolor: 'white',
        boxShadow: selected ? 5 : 3,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        animation: data.status === 'in_progress' ? `${pulseAnimation} 2s infinite` : 'none',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Input handle (left) for steps 2-7 */}
      {data.step > 1 && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: '#555' }}
        />
      )}

      {/* Node header */}
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <span>{statusIcon}</span>
          <span>Step {data.step}: {data.name}</span>
        </Typography>
      </Box>

      {/* Pending state */}
      {data.status === 'pending' && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
          Waiting...
        </Typography>
      )}

      {/* In Progress state */}
      {data.status === 'in_progress' && (
        <Box sx={{ mt: 1 }}>
          {data.progress !== undefined && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={data.progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF9800',
                  },
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                {Math.round(data.progress)}%
              </Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.message || 'Processing...'}
          </Typography>
          {elapsedTime && (
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                color: '#FF9800',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <span>⏱️</span>
              <span>{elapsedTime}</span>
            </Typography>
          )}
        </Box>
      )}

      {/* Completed state */}
      {data.status === 'completed' && (
        <Box sx={{ mt: 1 }}>
          <Chip
            label={`Duration: ${data.duration || 0}s`}
            color="success"
            size="small"
            sx={{
              fontSize: 10,
              height: 22,
              mb: 0.5,
            }}
          />
          {data.message && (
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                color: 'text.secondary',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.message}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: 'primary.main',
              display: 'block',
              mt: 0.5,
            }}
          >
            📊 Click for details
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {data.status === 'error' && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: 'error.main',
              display: 'block',
            }}
          >
            {data.message || 'Step failed'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: 'primary.main',
              display: 'block',
              mt: 0.5,
            }}
          >
            🔍 View error logs
          </Typography>
        </Box>
      )}

      {/* Output handle (right) for steps 1-6 */}
      {data.step < 7 && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: '#555' }}
        />
      )}
    </Box>
  );
};

export default PipelineStepNode;
